import { useState, useEffect, useRef } from 'react';
import { dashboardService, type MonitoredStudent } from '@/services/dashboardService';
import { useTeacherTheme } from '@/contexts/TeacherThemeContext';
import {
  LiveScreensLayout,
  ScreenTile,
  StudentScreenModal,
  type ScreenTileData,
  type FilterMode,
  type GridSize,
  POLL_INTERVAL,
  formatLastSeen,
} from '@/components/shared';

interface TeacherTile extends ScreenTileData {
  email: string;
  riskScore: number;
  lastActive: string | null;
}

export function TeacherLiveScreens() {
  const { dm } = useTeacherTheme();
  const [tiles, setTiles] = useState<TeacherTile[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [filter, setFilter] = useState<FilterMode>('all');
  const [gridSize, setGridSize] = useState<GridSize>('medium');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<TeacherTile | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      const studentsRes = await dashboardService.getMonitoredStudents(1, 200);

      const mapped: TeacherTile[] = studentsRes.data.map((s: MonitoredStudent) => ({
        id: s.id,
        name: s.name,
        email: s.email,
        riskScore: s.riskScore,
        alertCount: s.alertsThisMonth,
        lastActive: s.lastActive,
        currentDomain: s.currentDomain ?? null,
        currentEventType: s.currentEventType ?? null,
        currentPageTitle: s.currentPageTitle ?? null,
        currentUrl: s.currentUrl ?? null,
        isOnline: s.isActive ?? false,
        isFlagged: (s.isFlagged ?? false) || s.riskScore >= 70,
        timestampLabel: s.lastActive,
      }));

      setTiles(mapped);
      setLastRefresh(new Date());
    } catch {/* keep stale */} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const filtered = tiles.filter(t => {
    if (filter === 'online' && !t.isOnline) return false;
    if (filter === 'flagged' && !t.isFlagged) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!t.name.toLowerCase().includes(q) && !t.currentDomain?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const summary = {
    total: tiles.length,
    online: tiles.filter(t => t.isOnline).length,
    flagged: tiles.filter(t => t.isFlagged).length,
  };

  return (
    <>
      <LiveScreensLayout
        dm={dm}
        summary={summary}
        filter={filter}
        setFilter={setFilter}
        search={search}
        setSearch={setSearch}
        gridSize={gridSize}
        setGridSize={setGridSize}
        lastRefresh={lastRefresh}
        onRefresh={load}
        loading={loading}
        isEmpty={filtered.length === 0}
        filteredCount={filtered.length}
      >
        {filtered.map(tile => (
          <ScreenTile key={tile.id} tile={tile} dm={dm} gridSize={gridSize} onClick={() => setSelected(tile)} />
        ))}
      </LiveScreensLayout>

      {selected && (
        <StudentScreenModal
          name={selected.name}
          email={selected.email}
          isOnline={selected.isOnline}
          isFlagged={selected.isFlagged}
          currentDomain={selected.currentDomain}
          currentPageTitle={selected.currentPageTitle}
          currentUrl={selected.currentUrl}
          currentEventType={selected.currentEventType}
          statusText={selected.isOnline ? 'Online now' : selected.lastActive ? `Last seen ${formatLastSeen(selected.lastActive)}` : 'Offline'}
          statusDotClass={selected.isOnline ? 'bg-emerald-500' : 'bg-slate-400'}
          dm={dm}
          onClose={() => setSelected(null)}
          infoCells={[
            {
              label: 'Risk Score',
              value: `${selected.riskScore}%`,
              colorClass: selected.riskScore >= 70 ? 'text-red-500' : selected.riskScore >= 40 ? 'text-amber-500' : 'text-emerald-500',
            },
            {
              label: 'Alerts This Month',
              value: String(selected.alertCount),
              colorClass: selected.alertCount > 0 ? 'text-red-500' : undefined,
            },
            {
              label: 'Last Active',
              value: selected.lastActive
                ? new Date(selected.lastActive).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : 'Never',
            },
          ]}
        />
      )}
    </>
  );
}
